import type { InputHTMLAttributes, Ref } from 'react';
import { sanitizeNumeric } from './inputMask';

type NumericInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> & {
  value: string;
  onChange: (value: string) => void;
  /** Allow a single decimal point (e.g. money). Default false = integers only. */
  decimal?: boolean;
  /** React 19 passes `ref` as a plain prop; it reaches the <input> via the
   *  spread so a host can focus the field (the measurement card's corner
   *  handles do). */
  ref?: Ref<HTMLInputElement>;
};

/**
 * Controlled numeric input: opens the numeric keypad on mobile
 * (`inputMode="numeric"`, or `"decimal"` when `decimal` is set) and strips
 * anything that isn't a digit (or the one allowed dot). Use for quantities,
 * amounts, and other free numeric fields where `type="number"`'s scroll-wheel
 * / spinner behaviour is unwanted.
 */
export default function NumericInput({
  value,
  onChange,
  decimal = false,
  ...rest
}: NumericInputProps) {
  return (
    <input
      {...rest}
      type="text"
      inputMode={decimal ? 'decimal' : 'numeric'}
      value={value}
      onChange={(e) => onChange(sanitizeNumeric(e.target.value, decimal))}
    />
  );
}
